/**
 * Nav-mode visibility — the pure half of the shell's persona / industry
 * presentation filter.
 *
 * THE DEFECT THIS FILE EXISTS TO CLOSE
 * ------------------------------------
 * The persona and industry manifests in `@tyndo/types` name BARE modules
 * (`quality`, `production`) because they are authored against
 * `docs/module-catalog.json`. The running manifest registry — and therefore
 * `ModuleAccessInput.catalogueModuleIds`, which is what
 * `resolveModuleAccess` narrows — names PROVIDER-NAMESPACED modules
 * (`tyndo.quality`). `resolveModeVisibleModules` used to hand the bare names
 * straight to the gate, whose narrowing step is a plain `mode.has(id)` against
 * the namespaced id. That intersection is empty for every persona and every
 * industry bundle, so selecting persona (or industry) nav mode narrowed the
 * sidebar, browse catalogue, search and command palette to NOTHING.
 *
 * The fix is to translate the manifest's bare names into the catalogue's own id
 * space BEFORE the gate sees them, using the one `bareModuleId` helper
 * `personaWorkspace.ts` already documents and uses for exactly this mismatch.
 * There is deliberately no second normalisation rule in the shell.
 *
 * WHAT THIS IS NOT
 * ----------------
 * A presentation filter. The returned set can only ever NARROW what
 * `resolveModuleAccess` already permits — it is intersected with the entitled
 * set there, never substituted for it. A mode that selects a module the
 * principal is not entitled to still shows nothing, and a mode that selects
 * nothing at all is reported as an empty set (not `null`), because "this
 * persona has no modules in this deployment's catalogue" and "no mode filter is
 * active" are different answers.
 */
// Explicit `.ts` extension: this module is exercised by the shell's
// `node --experimental-strip-types --test` runner, whose ESM resolver does not
// perform bundler-style extensionless resolution. `allowImportingTsExtensions`
// is on in `tsconfig.base.json` and rspack resolves the explicit extension, so
// the same specifier works in the browser build.
import { bareModuleId } from './personaWorkspace.ts';

/**
 * Translate a manifest's module list into the catalogue's id space.
 *
 * `modeModules` is the persona / industry manifest list (bare names).
 * `catalogueModuleIds` is every module the manifest registry currently knows
 * about (provider-namespaced ids) — the widest possible set, and the same
 * snapshot the entitlement batch asked about.
 *
 * Returns `null` when there is no mode selection at all (isa95 / module modes,
 * or a mode whose persona / bundle is unset), which is how the gate spells "no
 * mode filter". Otherwise returns exactly the catalogue ids the mode selects.
 * Matching is on the bare form in BOTH directions, so it is correct whether the
 * catalogue is namespaced (`tyndo.quality`), bare (`quality`), or mixed.
 */
export function resolveModeVisibleCatalogueIds(
  modeModules: readonly string[] | null | undefined,
  catalogueModuleIds: readonly string[],
): Set<string> | null {
  if (!modeModules) return null;
  const wanted = new Set(modeModules.map(bareModuleId));
  return new Set(catalogueModuleIds.filter((id) => wanted.has(bareModuleId(id))));
}
